import { Suspense, type ComponentType, type LazyExoticComponent } from "react";

import { NotPhoneOptimisedNotice } from "./NotPhoneOptimisedNotice";
import { usePlatform, type Platform } from "./usePlatform";

/**
 * A routed page variant. Always lazy and platform-keyed, so a build never
 * fetches the tablet or phone chunk for a route that does not declare one.
 */
export type LazyComponent = LazyExoticComponent<ComponentType>;

export interface PlatformRouteProps {
  desktop: LazyComponent;
  tablet?: LazyComponent;
  phone?: LazyComponent;
}

/** Which variant a platform actually gets, and whether that is a fallback. */
export interface ResolvedVariant {
  platform: Platform;
  /** The tree the rendered file comes from. */
  resolved: Platform;
  fallback: boolean;
  /** A phone falling back says so; a tablet falling back is silent. */
  notice: boolean;
}

/**
 * Resolution, verbatim: the current platform's own variant if the route
 * declares one, otherwise DESKTOP. A missing variant is never a hidden nav
 * entry, a disabled route or a dead end.
 *
 * Pure, and a static fact of the route table — /dev/coverage enumerates it
 * without rendering anything.
 */
export function resolveVariant(props: PlatformRouteProps, platform: Platform): ResolvedVariant {
  const own = platform === "desktop" ? props.desktop : props[platform];
  if (own) return { platform, resolved: platform, fallback: false, notice: false };
  return { platform, resolved: "desktop", fallback: true, notice: platform === "phone" };
}

function PageFallback() {
  return (
    <div className="p-lg">
      <div className="h-4 w-40 animate-pulse rounded-sm bg-muted" />
    </div>
  );
}

export function PlatformRoute(props: PlatformRouteProps) {
  const platform = usePlatform();
  const { resolved, notice } = resolveVariant(props, platform);
  const Variant = resolved === "desktop" ? props.desktop : (props[resolved] ?? props.desktop);

  return (
    <>
      {notice ? <NotPhoneOptimisedNotice /> : null}
      <Suspense fallback={<PageFallback />}>
        <Variant />
      </Suspense>
    </>
  );
}
